import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { titleize, humanize, decapitalize } from 'underscore.string';
import Switch from 'react-switch';

import SessionStore from 'stores/SessionStore';
import CargomaticCard from 'components/v2/card';
import CargomaticModal from 'components/v2/modal';
import CargomaticSelect from 'components/v2/select';
import CargomaticInput from 'components/v2/input';
import UserWebAPIUtils from 'utils/UserWebAPIUtils';
import { getCreditCards } from 'utils/CustomersAPIUtils';
import LabelValue from 'components/ui/tailwind/label-value';
import { stateCodes } from 'components/modules/user/user-form-utils';
import ReferenceNumberTypes from 'constants/ReferenceNumberTypes';
import { OPS_GROUP_OPTIONS } from 'constants/shipper';
import {
  getAddressString,
  hasAddress,
} from 'utils/transformers/AddressTransformer';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import { unescapeSpecialChars } from 'src/main/js/helpers/helper_functions';
import Multiselect from 'components/v2/multi-select';
import { LINES_OF_BUSINESS } from 'config/lines-of-business';
import PhoneField from 'components/v2/forms/PhoneField';
import { createPhoneNumberWithExtension } from 'utils/formatters/Phone';
import UserStore from '../../../../stores/UserStore';
import ToastStore from '../../../../stores/ToastStore';
import UserActionCreators from '../../../../actions/UserActionCreators';
import EditButton from '../../../v2/buttons/EditButton';
import Badge from '../../../v2/Badge';
import Capability from '../Capability';
import CarrierPreferences from './carrier-preferences';
import { Avatar } from '../Avatar';

const MAX_MARKUP = 0.7;
const REQUIRED_ATTRIBUTES = [
  'first_name',
  'last_name',
  'username',
  'email',
  'street',
  'city',
  'state',
  'zipcode',
];
const MAX_TERM = 120;
const DEFAULT_PAYMENT_METHODS = [
  {
    name: 'Automated',
    value: 'automated',
    localizationSlug: 'AUTOMATED',
  },
  {
    name: 'Manual',
    value: 'manual',
    localizationSlug: 'MANUAL',
  },
];

const MODAL_TYPES = {
  EDIT: 'EDIT',
  SETTINGS: 'SETTINGS',
};

const getRequiredAsterisk = (field) => {
  return REQUIRED_ATTRIBUTES.includes(field) ? '*' : '';
};

const loginAs = async (grantorId) => {
  const admin = SessionStore.getUser();
  try {
    const user = await UserWebAPIUtils.loginAs(admin._id, grantorId).catch(
      (err) => {
        ToastStore.getNotifications().push({
          title: 'Error',
          body: err,
          level: 'error',
        });
      },
    );

    if (user) {
      window.location.href = '/';
    }
  } catch (err) {
    ToastStore.getNotifications().push({
      title: 'Error',
      body: err,
      level: 'error',
    });
  }
};

class ShipperDetails extends Component {
  static propTypes = {
    details: PropTypes.object,
    onSave: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      showAccountModal: false,
      showAddressModal: false,
      showSettingsModal: false,
      errors: {
        first_name: {},
        last_name: {},
        username: {},
        cdl: {},
        phone: {},
        phone_extension: {},
        markup: {},
        email: {},
        address: {},
        authorized: {},
        tsa: {},
        residential: {},
        test: {},
        require_tracking_id: {},
        business: {},
        requires_signature_capture: {},
        premium_shipper: {},
        cargomatic_transport: {},
        primary_reference_field: {},
        enterprise: {},
        strategic_shipper: {},
        project: {},
        beta_tester: {},
        black_and_white_invoices: {},
        invoice_schedule: {},
        drayage_invoice_branding: {},
        drayage_blling_time_frame: {},
        email_format: {},
        accountingHold: {},
        hide_column_properties: {},
      },
      changed: {},
      shipper: { ...props.details },
      eligiblePaymentMethods: [],
      hasLoginAsPermission: false,
      creditCardInfo: null,
      /*
      accountingHold: props.details.accounting
        ? props.details.accounting.on_hold
        : false,
      accountingHoldReason: props.details.accounting
        ? props.details.accounting.reason
        : '',
      accountingHoldError: false,
      */
    };
    UserActionCreators.getSalespeople();
    UserActionCreators.getAdminUsers({ active: true });
  }

  _onUserStoreChange = () => {
    const _salesReps = UserStore.getSalesReps() || [];
    const _activeAdmins = UserStore.getAdminUsers() || [];
    const salesReps = _salesReps.map((salesPerson) => ({
      slug: salesPerson.email,
      name: salesPerson.email,
    }));
    const activeAdminReps = _activeAdmins.map((activeAdmin) => ({
      slug: activeAdmin.email,
      name: activeAdmin.email,
    }));
    this.setState({
      salesReps,
      activeAdminReps: _.sortBy(activeAdminReps, 'name'),
    });
  };

  async componentDidMount() {
    try {
      UserStore.addChangeListener(this._onUserStoreChange);
      const creditCardInfo = await this.getCreditCardInfo();
      const hasLoginAsPermission = await this.hasLoginAsPermission();
      this.setState({
        creditCardInfo,
        hasLoginAsPermission,
      });
    } catch (e) {
      console.log('Error: ', e.message);
    }
  }

  componentWillUnmount() {
    UserStore.removeChangeListener(this._onUserStoreChange);
  }

  async UNSAFE_componentWillReceiveProps(nextProps) {
    const { showAccountModal, showAddressModal, showSettingsModal } =
      this.state;

    if (!showAccountModal && !showAddressModal && !showSettingsModal) {
      const shipper = nextProps.details;
      this.setState({
        shipper,
        hasLoginAsPermission: await this.hasLoginAsPermission(),
      });
    }
  }

  accountingAdminAccess() {
    return SessionStore.hasAccountingAdminRole() || SessionStore.isAdmin();
  }

  hasCreditCardOnFile() {
    return !!this.state.shipper?.stripe_customer_id;
  }

  async getCreditCardInfo() {
    if (this.hasCreditCardOnFile() && !this.state.creditCardInfo) {
      const {
        entity: { data },
      } = await getCreditCards(this.state.shipper?._id);
      return data[0];
    }
    return null;
  }

  async hasLoginAsPermission() {
    const { shipper } = this.state;
    const scopes = await UserWebAPIUtils.getUserContextGrantsByGrantee(
      shipper._id,
      ['login'],
    );
    if (scopes?.login) {
      return true;
    }
    return false;
  }

  validateFields = (parent, fields) => {
    const { t } = this.props;
    this.setState((prevState) => {
      const errors = { ...prevState.errors };
      fields.forEach((field) => {
        errors[field] =
          !parent[field] && REQUIRED_ATTRIBUTES.includes(field)
            ? {
                message: t('FIELD_REQUIRED_TIP', {
                  field: titleize(t(sluggify(field))),
                }),
              }
            : {};
      });
      return { errors };
    });
  };

  handleAddressFieldChange = (field, value) => {
    const { t } = this.props;
    this.setState((prevState) => {
      const shipper = { ...prevState.shipper };
      const changed = { ...prevState.changed };
      const errors = { ...prevState.errors };
      const address = shipper.address;

      address[field] = value;
      shipper.address = address;
      changed.address = true;
      errors[field] =
        !value && REQUIRED_ATTRIBUTES.includes(field)
          ? {
              message: t('FIELD_REQUIRED_TIP', {
                field: titleize(t(sluggify(field))),
              }),
            }
          : {};

      return {
        shipper,
        changed,
        errors,
      };
    });
  };

  handleFieldChange = (field, value) => {
    const { shipper, changed, errors } = this.state;
    const { t } = this.props;
    shipper[field] = value;
    changed[field] = true;
    errors[field] =
      !value && REQUIRED_ATTRIBUTES.includes(field)
        ? {
            message: t('FIELD_REQUIRED_TIP', {
              field: titleize(t(sluggify(field))),
            }),
          }
        : {};

    if (field === 'markup' && value && parseFloat(value) > MAX_MARKUP) {
      errors[field] = { message: t('MARK_UP_EXCEEED_MAX_TIP') };
    }
    if (field === 'drayage_terms' || field === 'shorthaul_terms') {
      if (Number(value) > MAX_TERM || Number(value) < 0) {
        errors[field] = {
          message: t('SHOULD_BE_BETWEEN_NUM1_NUM2', { num1: 0, num2: 20 }),
        };
      }
      if (Number(value) > MAX_TERM || Number(value) < 1) {
        errors[field] = {
          message: t('SHOULD_BE_BETWEEN_NUM1_NUM2', { num1: 1, num2: 120 }),
        };
      }
    }
    this.setState({ shipper, changed, errors });
  };

  handleArrayFieldChange = (field, value, enumValue) => {
    const { shipper, changed, errors } = this.state;
    const { t } = this.props;

    const fieldArray = shipper[field];
    shipper[field] = value
      ? fieldArray.concat([enumValue])
      : fieldArray.filter((column) => column !== enumValue);

    changed[field] = true;
    errors[field] =
      !value && REQUIRED_ATTRIBUTES.includes(field)
        ? {
            message: t('FIELD_REQUIRED_TIP', {
              field: titleize(t(sluggify(field))),
            }),
          }
        : {};

    this.setState({ shipper, changed, errors });
  };

  errorsExist() {
    const { errors } = this.state;
    let errorsExist = false;
    _.map(errors, (_value, key) => {
      if (!_.isEmpty(errors[key])) errorsExist = true;
    });
    return errorsExist;
  }

  onSave = () => {
    const { changed } = this.state;

    const { shipper } = this.state;
    const updates = _.pick(shipper, Object.keys(changed));

    if (updates.shorthaul_terms) {
      updates.shorthaul_terms = parseInt(updates.shorthaul_terms, 10);
    }
    if (updates.drayage_terms) {
      updates.drayage_terms = parseInt(updates.drayage_terms, 10);
    }

    updates._id = shipper._id;
    this.setState({ changed: {} }, () => {
      this.props.onSave(updates);
    });
  };

  renderIndustrySelect(industries) {
    const { shipper } = this.state;
    const { t } = this.props;
    const selected = shipper.industry ? shipper.industry : null;
    const items = industries.map((industry) => ({
      slug: industry.name,
      name: industry.name,
      localizationSlug: industry.localizationSlug,
    }));
    return (
      <CargomaticSelect
        selected={selected}
        items={items}
        label={t('INDUSTRY')}
        info={t('INDUSTRY_ACCOUNT_TIP')}
        valueKey="slug"
        textKey="localizationSlug"
        onSelect={(item) => this.handleFieldChange('industry', item.name)}
      />
    );
  }

  onChangeWatchingStatus = () => {
    this.setState(async (prevState) => {
      const {
        shipper: { status },
      } = prevState;
      const shipper = {
        ...prevState.shipper,
        status:
          status !== 'Service Watch' ? 'Service Watch' : 'Repeat Customer',
      };

      const payload = { _id: shipper._id, status: shipper.status };
      await this.props.onSave(payload);

      return {
        shipper,
      };
    });
  };

  onCancel = () => {
    const { details: shipper } = this.props; // Remove local changes that didn't save
    const { errors } = this.state;
    _.map(errors, (value, key) => {
      errors[key] = {};
    });
    this.setState({ shipper, changed: {}, errors }, () => {
      this.hideModal();
    });
  };

  handleLinesOfBusinessSelect = (linesOfBusiness) => {
    const shipper = {
      ...this.state.shipper,
      linesOfBusiness: linesOfBusiness.map((lob) => lob.slug),
    };
    const changed = {
      ...this.state.changed,
      linesOfBusiness: true,
    };
    this.setState((prevState) => {
      return { ...prevState, shipper, changed };
    });
  };

  renderFromAddress = (modal) => {
    if (
      this.props.details?.from_address_is_verified &&
      this.props.details?.from_address
    ) {
      const className = (
        modal === MODAL_TYPES.SETTINGS ? 'flex justify-between ' : ''
      ).concat('mb-2 -mx-4');
      const { t } = this.props;
      return (
        <div className={className}>
          <div className="w-1/2 px-4">
            <span className="form-label">{titleize(t('FROM_ADDRESS'))}</span>
          </div>
          <div className="px-4">
            <span className="text-gray-500">
              {this.props.details.from_address}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  renderModalComponent = () => {
    const industries = [
      {
        name: 'Manufacturing and Distribution',
        localizationSlug: 'MANUFACTURING_DISTRIBUTION',
      },
      { name: 'Carrier', localizationSlug: 'CARRIER' },
      { name: 'Freight forwarder', localizationSlug: 'FREIGHT_FORWARDER' },
      {
        name: 'Warehouse - 3PL - Broker',
        localizationSlug: 'WAREHOUSE_3PL_BROKER',
      },
    ];
    const { t } = this.props;
    const {
      salesReps,
      activeAdminReps,
      shipper: {
        first_name,
        last_name,
        company_name,
        markup,
        email,
        phone,
        phone_extension,
        address,
        require_tracking_id,
        business,
        requires_signature_capture,
        premium_shipper,
        cargomatic_transport,
        black_and_white_invoices,
        net_suite_id,
        shorthaul_terms,
        drayage_terms,
        drayage_payment_method,
        shorthaul_payment_method,
        enterprise,
        strategic_shipper,
        project,
        test,
        sales_rep,
        billing_rep,
        account_manager,
        operations_manager,
        customer_success_manager,
        operations_group,
        invoice_schedule,
        email_format,
        accounting,
        primary_reference_field,
        group_like_invoice_items,
        drayage_invoice_branding,
        drayage_blling_time_frame,
        hide_column_properties,
      },
      errors,
      showAccountModal,
      showAddressModal,
      showSettingsModal,
    } = this.state;

    const references = ReferenceNumberTypes.map((r) => {
      return {
        slug: r.name,
        name: titleize(humanize(r.name)),
        localizationSlug: r.localizationSlug,
      };
    });

    const linesOfBusiness = LINES_OF_BUSINESS.map((lob) => {
      return {
        ...lob,
        selected: this.state.shipper.linesOfBusiness.includes(lob.slug),
      };
    });

    const hasAdminOrShipperOnboardingRole =
      SessionStore.hasAdminRole() || SessionStore.hasShipperOnboardingRole();

    return (
      <div>
        {showAccountModal && (
          <div>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <CargomaticInput
                className="sm:col-span-6"
                error={errors._name}
                label={`${titleize(t('COMPANY_NAME'))} ${getRequiredAsterisk(
                  'company_name',
                )}`}
                info={t('SHIPPER_COMPANY_NAME')}
                onChange={(value) => {
                  this.handleFieldChange('company_name', value);
                }}
                value={unescapeSpecialChars(company_name)}
                maxLength={75}
              />
              <div className="sm:col-span-3">
                <CargomaticInput
                  error={errors.first_name}
                  label={`${titleize(t('FIRST_NAME'))} ${getRequiredAsterisk(
                    'first_name',
                  )}`}
                  info={t('SHIPPER_FIRST_NAME')}
                  onChange={(value) => {
                    this.handleFieldChange('first_name', value);
                  }}
                  value={unescapeSpecialChars(first_name)}
                />
              </div>
              <div className="sm:col-span-3">
                <CargomaticInput
                  error={errors.last_name}
                  label={`${titleize(t('LAST_NAME'))} ${getRequiredAsterisk(
                    'last_name',
                  )}`}
                  info={t('SHIPPER_LAST_NAME')}
                  onChange={(value) => {
                    this.handleFieldChange('last_name', value);
                  }}
                  value={unescapeSpecialChars(last_name)}
                />
              </div>
              <div className="sm:col-span-3">
                <label className="form-label mb-1">{`${titleize(
                  t('PHONE'),
                )} ${getRequiredAsterisk('phone')}`}</label>
                <PhoneField
                  id="phone"
                  onChange={(value) => this.handleFieldChange('phone', value)}
                  value={phone}
                />
              </div>
              <div className="sm:col-span-3">
                <CargomaticInput
                  className="sm:col-span-3"
                  error={errors.phone_extension}
                  label={` ${titleize(t('EXT'))} ${getRequiredAsterisk('')}`}
                  info={`${titleize(t('PHONE_NUMBER_EXTENSION_INFO'))}`}
                  onChange={(value) => {
                    this.handleFieldChange('phone_extension', value);
                  }}
                  value={phone_extension}
                />
              </div>
              <div className="sm:col-span-4">
                <CargomaticInput
                  error={errors.email}
                  label={`${titleize(t('EMAIL'))} ${getRequiredAsterisk(
                    'email',
                  )}`}
                  info={t('SHIPPER_EMAIL')}
                  onChange={(value) => this.handleFieldChange('email', value)}
                  value={email}
                  maxLength={254}
                />
              </div>
              <div className="sm:col-span-2">
                <CargomaticInput
                  error={errors.markup}
                  label={`${titleize(t('MARKUP'))} ${getRequiredAsterisk(
                    'markup',
                  )}`}
                  info={t('SHIPPER_MARKUP')}
                  onChange={(value) => this.handleFieldChange('markup', value)}
                  value={markup}
                />
              </div>
              <div className="sm:col-span-3">
                <label className="mb-1 form-label">
                  {t('LINES_OF_BUSINESS')}
                </label>
                <Multiselect
                  label={t('LINES_OF_BUSINESS')}
                  items={linesOfBusiness}
                  onSelect={this.handleLinesOfBusinessSelect}
                />
              </div>
            </div>
          </div>
        )}
        {showAddressModal && hasAdminOrShipperOnboardingRole && (
          <div>
            <h4 className="mt-0 mb-4 text-grey-darker">{t('ADDRESS_INFO')}</h4>
            <div className="grid sm:grid-cols-3 sm:gap-x-4 gap-y-6">
              <CargomaticInput
                className="sm:col-span-3"
                error={errors.street}
                label={`${titleize(t('STREET'))} ${getRequiredAsterisk(
                  'street',
                )}`}
                info={t('SHIPPER_STREET_ADDRESS')}
                onChange={(value) => {
                  this.handleAddressFieldChange('street', value);
                }}
                value={address.street}
              />
              <CargomaticInput
                className="sm:col-span-3"
                error={errors.street2}
                label={`${titleize(t('STREET2'))} ${getRequiredAsterisk(
                  'street2',
                )}`}
                info={t('SHIPPER_STREET2_ADDRESS')}
                onChange={(value) => {
                  this.handleAddressFieldChange('street2', value);
                }}
                value={address.street2}
              />
              <CargomaticInput
                error={errors.city}
                label={`${titleize(t('CITY'))} ${getRequiredAsterisk('city')}`}
                info={t('THE_SHIPPER_CITY')}
                onChange={(value) => {
                  this.handleAddressFieldChange('city', value);
                }}
                value={address.city}
              />
              <CargomaticSelect
                error={errors.state}
                selected={address.state}
                items={stateCodes}
                info={t('THE_SHIPPER_STATE')}
                label={`${titleize(t('STATE'))} ${getRequiredAsterisk(
                  'state',
                )}`}
                textKey="name"
                valueKey="abbreviation"
                onSelect={(item) => {
                  this.handleAddressFieldChange('state', item.abbreviation);
                }}
              />
              <CargomaticInput
                error={errors.zipcode}
                label={`${titleize(t('ZIP_CODE'))} ${getRequiredAsterisk(
                  'zipcode',
                )}`}
                info={t('SHIPPER_POSTAL_CODE')}
                onChange={(value) => {
                  this.handleAddressFieldChange('zipcode', value);
                }}
                value={address.zipcode}
              />
            </div>
          </div>
        )}
        {showSettingsModal && (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
              <CargomaticInput
                id="require_tracking_id"
                type="checkbox"
                error={errors.require_tracking_id}
                label={`${t('REFERENCE_NUMBER_REQUIRED')} ${getRequiredAsterisk(
                  'require_tracking_id',
                )}`}
                info={t('TRACKING_ID')}
                onChange={(value) => {
                  this.handleFieldChange('require_tracking_id', value);
                }}
                value={require_tracking_id}
              />
              <CargomaticInput
                id="business"
                type="checkbox"
                error={errors.business}
                label={`${titleize(t('BUSINESS'))} ${getRequiredAsterisk(
                  'business',
                )}`}
                info={t('B2B_SHIPPER')}
                onChange={(value) => this.handleFieldChange('business', value)}
                value={business}
              />
              <CargomaticInput
                id="requires_signature_capture"
                type="checkbox"
                error={errors.requires_signature_capture}
                label={`${t(
                  'REQUIRES_SIGNATURE_CAPTURE',
                )} ${getRequiredAsterisk('requires_signature_capture')}`}
                info={t('SIGNATURE_CAPTURE_TIP')}
                onChange={(value) => {
                  this.handleFieldChange('requires_signature_capture', value);
                }}
                value={requires_signature_capture}
              />
              <CargomaticInput
                id="premium_shipper"
                type="checkbox"
                error={errors.premium_shipper}
                label={`${t('PREMIUM_SHIPPER')} ${getRequiredAsterisk(
                  'premium_shipper',
                )}`}
                info={t('PREMIUM_SHIPPER')}
                onChange={(value) => {
                  this.handleFieldChange('premium_shipper', value);
                }}
                value={premium_shipper}
              />
              <CargomaticInput
                id="cargomatic_transport"
                type="checkbox"
                error={errors.cargomatic_transport}
                label={`${t('CARGOMATIC_TRANSPORT')} ${getRequiredAsterisk(
                  'cargomatic_transport',
                )}`}
                info={t('INVOICE_ENTITY_FOR_ACCOUNT')}
                onChange={(value) => {
                  this.handleFieldChange('cargomatic_transport', value);
                }}
                value={cargomatic_transport}
              />
              <CargomaticInput
                id="enterprise"
                type="checkbox"
                error={errors.enterprise}
                label={`${t('ENTERPRISE_SHIPPER')} ${getRequiredAsterisk(
                  'enterprise',
                )}`}
                info={t('ENTERPRISE_ACCOUNT')}
                onChange={(value) => {
                  this.handleFieldChange('enterprise', value);
                }}
                value={enterprise}
              />
              <CargomaticInput
                id="strategic_shipper"
                type="checkbox"
                error={errors.strategic_shipper}
                label={`${t('STRATEGIC_SHIPPER')} ${getRequiredAsterisk(
                  'strategic_shipper',
                )}`}
                info={t('STRATEGIC_ACCOUNT')}
                onChange={(value) => {
                  this.handleFieldChange('strategic_shipper', value);
                }}
                value={strategic_shipper}
              />
              <CargomaticInput
                id="project"
                type="checkbox"
                error={errors.project}
                label={`${t('PROJECT_SHIPPER')} ${getRequiredAsterisk(
                  'project',
                )}`}
                info={t('PROJECT_ACCOUNT')}
                onChange={(value) => this.handleFieldChange('project', value)}
                value={project}
              />
              <CargomaticInput
                id="test"
                type="checkbox"
                error={errors.test}
                label={`${t('TEST_USER')} ${getRequiredAsterisk('test')}`}
                info={t('TEST_ACCOUNT')}
                onChange={(value) => this.handleFieldChange('test', value)}
                value={test}
              />
              <CargomaticInput
                id="hide_driver_property"
                type="checkbox"
                label={`${t('HIDE_DRIVER_PROPERTY')} ${getRequiredAsterisk(
                  'HIDE_DRIVER_PROPERTY',
                )}`}
                info={t('HIDE_DRIVER_DETAILS_SHIPPER_PORTAL')}
                value={hide_column_properties.includes('driver')}
                onChange={(value) =>
                  this.handleArrayFieldChange(
                    'hide_column_properties',
                    value,
                    'driver',
                  )
                }
              />
              <CargomaticInput
                id="hide_carrier_property"
                type="checkbox"
                label={`${t('HIDE_CARRIER_PROPERTY')} ${getRequiredAsterisk(
                  'HIDE_CARRIER_PROPERTY',
                )}`}
                info={t('HIDE_CARRIER_DETAILS_SHIPPER_PORTAL')}
                value={hide_column_properties.includes('carrier')}
                onChange={(value) =>
                  this.handleArrayFieldChange(
                    'hide_column_properties',
                    value,
                    'carrier',
                  )
                }
              />
            </div>
            <hr />
            <div className="mt-4 grid sm:grid-cols-2 sm:gap-4">
              <CargomaticSelect
                selected={email_format}
                items={[
                  { slug: 'html', name: 'HTML', localizationSlug: 'HTML' },
                  { slug: 'text', name: 'Text', localizationSlug: 'TEXT' },
                ]}
                label={t('EMAIL_FORMAT_FOR_NOTIFICATION')}
                info={t('INVOICE_EMAIL_FORMAT')}
                valueKey="slug"
                textKey="localizationSlug"
                onSelect={(item) => {
                  this.handleFieldChange('email_format', item.slug);
                }}
              />
              {this.renderIndustrySelect(industries)}
              {this.accountingAdminAccess() && [
                <CargomaticSelect
                  selected={primary_reference_field}
                  items={references}
                  label={t('PRIMARY_REFERENCE_FIELD')}
                  info={t('PRIMARY_REFERENCE_FIELD_TIP')}
                  valueKey="slug"
                  textKey="localizationSlug"
                  onSelect={(item) => {
                    this.handleFieldChange(
                      'primary_reference_field',
                      item.slug,
                    );
                  }}
                />,
              ]}
              {this.renderFromAddress(MODAL_TYPES.EDIT)}
            </div>
          </div>
        )}
        <div className="flex justify-end my-2">
          <button
            className="justify-center button button-secondary"
            onClick={this.onCancel}
          >
            {t('CANCEL')}
          </button>
          <button
            className="mx-2 button button-green"
            onClick={this.onSave}
            disabled={this.errorsExist()}
          >
            {titleize(t('SAVE'))}
          </button>
        </div>
      </div>
    );
  };

  renderAddress = () => {
    const { t } = this.props;
    const { address } = this.props.details;
    const addressString = getAddressString(address);
    return (
      <CargomaticCard
        header={() => (
          <header
            className="cursor-pointer group"
            onClick={this.handleShowEditAddressModal}
          >
            <div className="flex items-center justify-between -mx-2">
              <h3 className="px-2 my-0">{titleize(t('ADDRESS'))}</h3>
              <EditButton />
            </div>
          </header>
        )}
      >
        <div className="mb-6">
          {hasAddress(address) && (
            <LabelValue value={addressString} icon="map-marker" />
          )}
          {!hasAddress(address) && (
            <LabelValue value={t('NO_ADDRESS_ON_FILE')} icon="map-marker" />
          )}
        </div>
      </CargomaticCard>
    );
  };

  renderSettings = () => {
    const {
      require_tracking_id,
      business,
      requires_signature_capture,
      premium_shipper,
      cargomatic_transport,
      black_and_white_invoices,
      enterprise,
      strategic_shipper,
      project,
      test,
      invoice_schedule,
      email_format,
      accounting,
      industry,
      net_suite_id,
      hide_column_properties,
    } = this.props.details;
    const { t } = this.props;
    /*
    const { errors, shipper } = this.state;
    */
    return (
      <CargomaticCard
        header={() => (
          <header
            className="cursor-pointer group"
            onClick={this.handleShowEditSettingsModal}
          >
            <div className="flex items-center justify-between -mx-2">
              <h3 className="px-2 my-0">{titleize(t('SETTINGS'))}</h3>
              <EditButton />
            </div>
          </header>
        )}
      >
        <Capability
          label={t('REQUIRE_TRACKING_ID')}
          checked={require_tracking_id}
        />
        <Capability label={t('B2B_ACCOUNT')} checked={business} />
        <Capability
          label={t('REQUIRES_SIGNATURE_CAPTURE')}
          checked={requires_signature_capture}
        />
        <Capability label={t('PREMIUM_SHIPPER')} checked={premium_shipper} />
        <Capability
          label={t('CARGOMATIC_TRANSPORT')}
          checked={cargomatic_transport}
        />
        <Capability label={t('ENTERPRISE_SHIPPER')} checked={enterprise} />
        <Capability
          label={t('STRATEGIC_SHIPPER')}
          checked={strategic_shipper}
        />
        <Capability label={t('PROJECT_SHIPPER')} checked={project} />
        <Capability label={t('TEST_ACCOUNT')} checked={test} />
        <Capability
          label={t('HIDE_DRIVER_PROPERTY')}
          checked={hide_column_properties.includes('driver')}
        />
        <Capability
          label={t('HIDE_CARRIER_PROPERTY')}
          checked={hide_column_properties.includes('carrier')}
        />
        <Capability
          label={t('BLACK_AND_WHITE_INVOICES')}
          checked={black_and_white_invoices}
        />
        <hr />
        <div className="flex">
          <div className="w-full">
            <div className="flex justify-between mb-2 -mx-4">
              <div className="w-1/2 px-4">
                <span className="form-label">{t('EMAIL_FORMAT')}</span>
              </div>
              <div className="px-4">
                <span className="text-gray-500">
                  {titleize(t(sluggify(email_format)))}
                </span>
              </div>
            </div>
            <div className="flex justify-between mb-2 -mx-4">
              <div className="w-1/2 px-4">
                <span className="form-label">{t('INDUSTRY')}</span>
              </div>
              <div className="px-4">
                <span className="text-gray-500">
                  {industry ? titleize(t(sluggify(industry))) : t('NOT_SET')}
                </span>
              </div>
            </div>
            {this.renderFromAddress(MODAL_TYPES.SETTINGS)}
          </div>
        </div>
      </CargomaticCard>
    );
  };

  handleShowEditAccountModal = () => {
    this.setState({
      showSettingsModal: false,
      showAccountModal: true,
      showAddressModal: false,
    });
  };

  handleShowEditAddressModal = () => {
    // Handle possible partial address from brand site signup
    this.validateFields(this.state.shipper.address, [
      'street',
      'street2',
      'city',
      'state',
      'zipcode',
    ]);
    this.setState({
      showSettingsModal: false,
      showAccountModal: false,
      showAddressModal: true,
    });
  };

  handleShowEditSettingsModal = () => {
    this.setState({
      showSettingsModal: true,
      showAccountModal: false,
      showAddressModal: false,
    });
  };

  hideModal() {
    this.setState({
      showSettingsModal: false,
      showAccountModal: false,
      showAddressModal: false,
    });
  }

  onHide = () => {
    if (!this.errorsExist()) this.hideModal();
  };

  getAccountValue(value) {
    const { t } = this.props;
    return value && value?.toLowerCase() !== 'none'
      ? value
      : decapitalize(t('NONE'));
  }

  render() {
    /*
    const industries = [
      'Manufacturing and Distribution',
      'Carrier',
      'Freight forwarder',
      'Warehouse - 3PL - Broker',
    ];
    */
    // const canEditNetSuiteID = isAdmin || SessionStore.hasFinanceRole();
    // const validationErrors = this.getValidationErrors();

    const {
      details: {
        sales_rep,
        account_manager,
        operations_manager,
        billing_rep,
        status,
        first_name,
        last_name,
        company_name,
        email,
        phone,
        phone_extension,
        /*
        require_tracking_id,
        business,
        */
        markup,
        /*
        requires_signature_capture,
        premium_shipper,
        cargomatic_transport,
        can_pay_manually,
        enterprise,
        strategic_shipper,
        project,
        beta_tester,
        test,
        black_and_white_invoices,
        invoice_schedule,
        email_format,
        accountingHold,
        */
      },
      t,
    } = this.props;
    const {
      showAccountModal,
      showAddressModal,
      showSettingsModal,
      hasLoginAsPermission /* , errors */,
    } = this.state;

    const { userId } = this.props;

    const canWatch = status !== 'New Customer';

    const statusColor = status === 'Service Watch' ? 'brand' : 'blue';

    return (
      <div>
        <CargomaticCard>
          <div className="text-center">
            <div className="flex justify-center mb-2">
              <Avatar
                key={userId}
                userId={userId}
                className="h-32 w-32 cursor-pointer rounded-full"
              />
            </div>
            <h3 className="my-2 text-xl text-grey-darker">
              {unescapeSpecialChars(company_name)}
            </h3>
            <div className="flex items-center justify-center -mx-1">
              <div className="px-1 mb-2">
                <Badge color="green" rounded size="large">
                  {titleize(t('SHIPPER'))}
                </Badge>
              </div>
              <div className="px-1 mb-2">
                <Badge color={statusColor} rounded size="large">
                  {status === 'Service Watch'
                    ? t('SERVICE_WATCH')
                    : t('REPEAT_CUSTOMER')}
                </Badge>
              </div>
            </div>
            <div className="flex justify-center mt-4 -mx-4">
              <button
                className="button text-grey-darker px-0 mr-2"
                onClick={this.props.onOpenJobs}
              >
                {t('VIEW_JOBS')}
              </button>
              {canWatch && (
                <div className="flex items-center px-2">
                  <label className="my-0 mr-2 form-label">
                    {titleize(t('WATCH'))}
                  </label>
                  <Switch
                    onColor="#21b88e"
                    height={14}
                    width={28}
                    checked={status === 'Service Watch'}
                    onChange={() => this.onChangeWatchingStatus()}
                    className=""
                  />
                </div>
              )}
              {SessionStore.isSupport() && (
                <button
                  className="button text-grey-darker px-0 ml-2 "
                  onClick={() => loginAs(this.state.shipper._id)}
                >
                  {t('LOG_IN_AS')}
                </button>
              )}
            </div>
          </div>
        </CargomaticCard>

        <div className="mt-6">
          <CargomaticCard
            header={() => (
              <header
                className="cursor-pointer group"
                onClick={this.handleShowEditAccountModal}
              >
                <div className="flex items-center justify-between">
                  <h3 className="my-0">{titleize(t('ACCOUNT'))}</h3>
                  <EditButton />
                </div>
              </header>
            )}
          >
            <LabelValue
              value={`${unescapeSpecialChars(company_name)}`}
              icon="home"
            />
            <LabelValue
              value={`${unescapeSpecialChars(
                first_name,
              )} ${unescapeSpecialChars(last_name)}`}
              icon="user"
            />
            <LabelValue
              value={createPhoneNumberWithExtension(
                phone,
                phone_extension,
                t('EXT'),
              )}
              icon="phone"
            />
            <LabelValue value={`${email}`} icon="envelope" />
            <LabelValue value={`${markup}`} icon="usd" />
            <LabelValue
              value={this.getAccountValue(sales_rep)}
              icon="trending-up"
            />
            <LabelValue
              value={this.getAccountValue(account_manager)}
              icon="account_manager"
            />
            <LabelValue
              value={this.getAccountValue(operations_manager)}
              icon="operations_manager"
            />
          </CargomaticCard>
        </div>

        <div className="mt-6">{this.renderAddress()}</div>

        <div className="mt-6">
          <CarrierPreferences shipper={this.props.details} />
        </div>

        <div className="mt-6">{this.renderSettings()}</div>

        <div className="my-6">
          <button
            onClick={() => this.props.onOpenPassword()}
            className="w-full button button-hollow-red"
          >
            {t('CHANGE_PASSWORD')}
          </button>
        </div>
        {SessionStore.isSupport() && !hasLoginAsPermission && (
          <div className="my-6">
            <button
              onClick={() => this.props.onOpenAccountRequest()}
              className="w-full button button-green"
            >
              {t('REQUEST_PERMISSION_TO_LOGIN_AS')}
            </button>
          </div>
        )}
        {(showAccountModal || showAddressModal || showSettingsModal) && (
          <CargomaticModal
            show
            onHide={this.onHide}
            title={t('EDIT_ACCOUNT')}
            backdrop="static"
            body={this.renderModalComponent()}
            footer={null}
            location="middle"
          />
        )}
      </div>
    );
  }
}

export default withTranslation()(ShipperDetails);
